import gql from "graphql-tag";
import { formatDate_YMDHMS, roundDateToFloor } from "@/_core/_helpers/_helpers";
import { getHistoricalData } from "@/_core/data/api_calls/candle_data/getHistoricalData";
import { apiService } from "@/_services";

/**
 * Retrieves the spot price for a commodity, either from Redis or an API call.
 *
 * @param {object} _this - Context object containing necessary data.
 * @param {string} fieldName - Field name to store the retrieved spot price.
 * @param {string} fieldUpdatedAtName - Field name for storing the timestamp of the update.
 * @param {string} fieldShowLoadingName - Field name to indicate if loading should be displayed.
 * @param {string} [strip=null] - Field determining whether the request is for the whole strip (for commodities only).
 * @param {boolean} [isContract=false] - Determines if the request is for a contract-based price.
 * @returns {Promise<object>} - A promise resolving to the spot price data.
 */
export function getSpotCommodity(
  _this,
  fieldName,
  fieldUpdatedAtName,
  fieldShowLoadingName,
  strip = null,
  isContract = false
) {
  let fast = true; // Determines whether to use Redis caching or query the API directly.
  if (fast) {
    // Define the GraphQL query for retrieving data from Redis.
    const paramsRedis = gql`
      query($key: String!) {
        getRedisData(key: $key)
      }
    `;
    let keySpot = "";
    // Determine the commodity name based on available context.
    if (_this.commodityDefs) {
      keySpot = _this.commodityDefs.commodity_name;
    } else if (_this.commodityString) {
      keySpot = _this.commodityString;
    } else if (
      _this.commoditySpecs &&
      _this[_this.fieldA] &&
      _this.commoditySpecs[_this[_this.fieldA][1]]
    ) {
      keySpot = _this.commoditySpecs[_this[_this.fieldA][1]].commodity_name;
    }
    // Set loading state for UI.
    _this[fieldShowLoadingName] = !isContract;
    _this[fieldName] = 0;
    let contract = _this.contract;
    // Adjust contract handling based on route and contract flag.
    if (_this.$route.path === "/optimizer" || isContract) {
      contract = undefined;
    } else if (_this.contract === undefined) {
      _this.contract = 1;
      contract = 1;
    }
    // Format the commodity name as an uppercase key for Redis lookup.
    keySpot = keySpot.replaceAll(" ", "_").toUpperCase();
    // Define the parameters for querying Redis.
    const variablesRedis = {
      format: "json",
      key: `SPOT_COM_${keySpot}_${contract}`,
      observation_date: formatDate_YMDHMS(
        roundDateToFloor(_this.initCurrencyDate)
      )
    };
    // Attempt to fetch spot price data from Redis first.
    return getHistoricalData(_this, {
      params: paramsRedis,
      variables: variablesRedis,
      source: "getRedisData"
    }).catch(err => {
      console.error("Error from getRedisData:", err);
      Object.assign(_this, { faildGetHistoricalData: true });
      // If Redis data retrieval fails, fall back to API request.
      const commodityNumber = _this.commodityNumber
        ? _this.commodityNumber
        : _this.commoditySpecs[_this[_this.fieldA][1]].pk_idcommodity;
      // Define the parameters for querying the API.
      const params = {
        method: "GET",
        endpoint: "commodities-intraday-history",
        content_type: "application/json",
        query_params: {
          format: "json",
          last: 1,
          commodity: commodityNumber,
          contract: strip == null ? _this.contract : 0
        }
      };
      // Fetch spot price data from the API if Redis is unavailable.
      return apiService.getAll(params);
    });
  } else {
    console.error("Not using redis, Warning!");
    Object.assign(_this, { faildGetHistoricalData: true });
    // If Redis is disabled, query the API directly.
    const commodityNumber = _this.commodityNumber
      ? _this.commodityNumber
      : _this.commoditySpecs[_this[_this.fieldA][1]].pk_idcommodity;
    const params = {
      method: "GET",
      endpoint: "commodities-intraday-history",
      content_type: "application/json",
      query_params: {
        format: "json",
        last: 1,
        commodity: commodityNumber,
        contract: strip == null ? _this.contract : 0
      }
    };
    // Fetch the spot price data from the API.
    return apiService.getAll(params);
  }
}
